import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, getRelatedMovies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const [movie, setMovie] = useState<Movie | null>(null);
  const [related, setRelated] = useState<Movie[]>([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [id]);

  useEffect(() => {
    if (id) {
      const found = getMovieById(id);
      setMovie(found || null);
      if (found) {
        setRelated(getRelatedMovies(found, 6));
      }
    }
  }, [id]);

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-20">
          <div className="text-center">
            <span className="w-20 h-20 flex items-center justify-center rounded-2xl bg-background-800 mx-auto mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-500"></i>
            </span>
            <p className="text-foreground-400 text-lg">影片未找到</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-4 px-5 py-2 rounded-full bg-primary-500 text-background-950 text-sm font-label font-semibold hover:bg-primary-400 transition-all cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const detailSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    dateCreated: `${movie.year}`,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.actors.map((a) => ({
      '@type': 'Person',
      name: a,
    })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      ratingCount: movie.views,
    },
  };

  return (
    <>
      <SEO
        title={`${movie.title} - 被窝影院高清在线观看`}
        description={`${movie.title}(${movie.year}) ${movie.description.substring(0, 100)} - 被窝影院提供${movie.title}高清免费在线观看，导演${movie.director}，主演${movie.actors.join('、')}。`}
        keywords={`${movie.title},${movie.title}在线观看,${movie.title}高清,${movie.genre.join(',')},被窝影院`}
        canonicalUrl={`https://www.beiwoyingyuan.com/detail/${movie.id}`}
        ogImage={movie.poster}
        ogType="video.movie"
        schema={detailSchema}
      />

      <Navbar />

      <main>
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: movie.category === 'tv' ? '电视剧' : movie.category === 'anime' ? '动漫' : movie.category === 'variety' ? '综艺' : '电影', href: `/category/${movie.category}` },
            { label: movie.title },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
              <div className="flex-shrink-0 w-full sm:w-64 md:w-72 mx-auto lg:mx-0">
                <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-800">
                  <img
                    src={movie.poster}
                    alt={movie.title}
                    title={`${movie.title} - 海报`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1 min-w-0">
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-100 mb-1">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-400 mb-4">{movie.enTitle}</p>

                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="text-sm px-3 py-1 rounded-full bg-accent-500/15 text-accent-400 font-label font-semibold">
                    {movie.rating} 分
                  </span>
                  {movie.isNew && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-accent-500 text-background-950 font-label font-semibold">
                      最新
                    </span>
                  )}
                  {movie.isHot && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-primary-500/20 text-primary-400 font-label font-semibold border border-primary-500/30">
                      热播
                    </span>
                  )}
                  <span className="text-sm text-foreground-400">{movie.views.toLocaleString()} 次观看</span>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: '年份', value: `${movie.year}年` },
                    { label: '地区', value: movie.region },
                    { label: '类型', value: movie.genre.join(' / ') },
                    { label: '时长', value: movie.duration },
                  ].map((item) => (
                    <div key={item.label} className="p-3 rounded-lg bg-background-900/50 border border-background-800/50">
                      <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                      <p className="text-sm text-foreground-200 font-label">{item.value}</p>
                    </div>
                  ))}
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-label text-foreground-300 mb-1.5">导演</h4>
                  <p className="text-sm text-foreground-200">{movie.director}</p>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-label text-foreground-300 mb-1.5">主演</h4>
                  <div className="flex flex-wrap gap-2">
                    {movie.actors.map((actor) => (
                      <span
                        key={actor}
                        className="text-xs px-3 py-1 rounded-full bg-background-800 text-foreground-300"
                      >
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3 mb-6">
                  <a
                    href="#"
                    className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-950 text-sm font-label font-semibold hover:bg-primary-400 transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-play-circle-fill text-lg"></i>
                    立即播放
                  </a>
                  <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-600 text-foreground-300 text-sm font-label hover:bg-background-800 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-heart-line text-base"></i>
                    收藏
                  </button>
                  <button className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-background-600 text-foreground-300 text-sm font-label hover:bg-background-800 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-share-forward-line text-base"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-8 md:mt-10">
              <h2 className="text-lg font-heading font-bold text-foreground-100 mb-3">剧情简介</h2>
              <p className="text-sm text-foreground-400 leading-relaxed max-w-3xl">
                {movie.description}
              </p>
            </div>

            {related.length > 0 && (
              <div className="mt-10 pt-8 border-t border-background-800/50">
                <h2 className="text-lg font-heading font-bold text-foreground-100 mb-5">相关推荐</h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                  {related.map((m) => (
                    <MovieCard key={m.id} movie={m} variant="poster" />
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}